import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { normalizeClientInfoKind } from "./skillAgentClientInfoKind";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import {
  deleteSkillAgentClientInfo,
  deleteSkillAgentClientInfoItem,
  fetchSkillAgentClientInfoItemBlob,
  getSkillAgentClientInfoById,
  updateSkillAgentClientInfo,
  uploadSkillAgentClientInfoItems,
} from "../../api/Skills/skillAgentClientInfosApi";
import SkillAgentInlineTitle from "./SkillAgentInlineTitle";
import SkillConfirmDialog from "./SkillConfirmDialog";
import MeetingDeleteMediaDialog from "../Meetings/MeetingDeleteMediaDialog";

function kindIcon(kind) {
  if (kind === "pdf") return PictureAsPdfOutlinedIcon;
  if (kind === "image") return ImageOutlinedIcon;
  if (kind === "video") return MovieOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

function kindColor(kind) {
  if (kind === "pdf") return "#c62828";
  if (kind === "image") return "#00897b";
  if (kind === "video") return "#6a1b9a";
  return "#546e7a";
}

export default function SkillAgentClientInfoDetail({
  clientInfoId,
  mode: modeProp,
  onChanged,
  onDeleted,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState("");
  const [previewKind, setPreviewKind] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [imgZoom, setImgZoom] = useState(1);

  const load = useCallback(async () => {
    if (!clientInfoId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSkillAgentClientInfoById(clientInfoId);
      setInfo(data);
      setTitulo(data?.Titulo || "");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.clientInfo.loadError", {
            defaultValue: "No se pudo abrir Client Info.",
          }),
      );
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [clientInfoId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const dossierKind = normalizeClientInfoKind(
    modeProp || info?.Kind || info?.kind,
  );
  const isProduccion = dossierKind === "produccion";
  const isPresentaciones = dossierKind === "presentaciones_pdf";
  const isStatus = dossierKind === "status";
  const dossierLabel = isStatus
    ? "STATUS"
    : isPresentaciones
      ? "PRESENTACIONES PDF"
      : isProduccion
        ? "PRODUCCIÓN"
        : "CLIENT INFO";
  const uploadAccept = isPresentaciones
    ? "application/pdf,.pdf"
    : "image/*,application/pdf,video/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";

  const openItem = async (item) => {
    if (!item?.id) return;
    setBusy(true);
    setError("");
    try {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const blob = await fetchSkillAgentClientInfoItemBlob(
        clientInfoId,
        item.id,
      );
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewMime(item.MimeType || blob.type || "");
      setPreviewKind(item.Kind || "other");
      setImgZoom(1);
      setActiveId(item.id);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.clientInfo.previewError", {
            defaultValue: "No se pudo abrir el archivo.",
          }),
      );
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || !clientInfoId) return;
    setBusy(true);
    setError("");
    try {
      const data = await uploadSkillAgentClientInfoItems(clientInfoId, files);
      const uploaded = Array.isArray(data?.items) ? data.items : [];
      setItems((prev) => [...prev, ...uploaded]);
      onChanged?.();
      if (uploaded[0]) openItem(uploaded[0]);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.clientInfo.uploadError", {
            defaultValue: "No se pudieron subir los archivos.",
          }),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteItemConfirm = async () => {
    const item = deleteItem;
    if (!item?.id || busy) return;
    setBusy(true);
    setError("");
    try {
      await deleteSkillAgentClientInfoItem(clientInfoId, item.id);
      setItems((prev) => prev.filter((x) => Number(x.id) !== Number(item.id)));
      if (Number(activeId) === Number(item.id)) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setActiveId(null);
      }
      setDeleteItem(null);
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.clientInfo.deleteItemError", {
            defaultValue: "No se pudo borrar el archivo.",
          }),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteInfo = async () => {
    if (!clientInfoId || busy) return;
    setBusy(true);
    try {
      await deleteSkillAgentClientInfo(clientInfoId);
      setDeleteOpen(false);
      onDeleted?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.clientInfo.deleteError", {
            defaultValue: "No se pudo borrar.",
          }),
      );
      setBusy(false);
    }
  };

  const isImagePreview =
    previewKind === "image" || String(previewMime).startsWith("image/");

  const deleteItemBadge = (() => {
    const k = String(deleteItem?.Kind || "").toLowerCase();
    if (k === "pdf") return "PDF";
    if (k === "video") return "VIDEO";
    if (k === "image") return "PHOTO";
    return "FILE";
  })();

  if (loading && !info) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!info) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {error ||
          t(
            isProduccion
              ? "skillAgent.produccion.notFound"
              : "skillAgent.clientInfo.notFound",
            {
              defaultValue: isProduccion
                ? "Producción no encontrada."
                : "Client Info no encontrado.",
            },
          )}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          mx: { xs: 1.5, md: 2 },
          mt: { xs: 1.5, md: 2 },
          py: 0.85,
          px: 1,
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: (th) => alpha(th.palette.primary.main, 0.12),
          bgcolor: (th) => alpha(th.palette.background.paper, 0.96),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <SkillAgentInlineTitle
          entityKey={clientInfoId}
          value={titulo}
          onChange={setTitulo}
          onCommit={async (next) => {
            if (!clientInfoId || next === String(info?.Titulo || "").trim()) {
              return;
            }
            setBusy(true);
            try {
              const updated = await updateSkillAgentClientInfo(clientInfoId, {
                Titulo: next,
              });
              setInfo((p) => ({ ...p, ...updated }));
              setTitulo(updated?.Titulo || next);
              onChanged?.();
            } catch (err) {
              setError(err?.response?.data?.error || "Error al guardar");
              setTitulo(info?.Titulo || "");
            } finally {
              setBusy(false);
            }
          }}
          placeholder={dossierLabel}
          disabled={busy}
          sx={{ maxWidth: { xs: "100%", md: 420 } }}
        />
        {info?.DriveFolderUrl ? (
          <Tooltip
            title={t("skillAgent.clientInfo.openDrive", {
              defaultValue: "Abrir carpeta en Drive",
            })}
          >
            <IconButton
              size="small"
              component="a"
              href={info.DriveFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              disabled={busy}
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip
          title={t(
            isStatus
              ? "skillAgent.status.deleteTooltip"
              : isPresentaciones
                ? "skillAgent.presentaciones.deleteTooltip"
                : isProduccion
                  ? "skillAgent.produccion.deleteTooltip"
                  : "skillAgent.clientInfo.deleteTooltip",
            {
              defaultValue: isStatus
                ? "Borrar Status"
                : isPresentaciones
                  ? "Borrar Presentaciones PDF"
                  : isProduccion
                    ? "Borrar Producción"
                    : "Borrar Client Info",
            },
          )}
        >
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={busy}
              onClick={() => setDeleteOpen(true)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept={uploadAccept}
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>

      {error ? (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mx: { xs: 1.5, md: 2 }, mt: 1, flexShrink: 0 }}
        >
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 280px) minmax(0, 1fr)",
          },
          gridTemplateRows: { xs: "auto minmax(0, 1fr)", md: "minmax(0, 1fr)" },
          gap: 1.5,
          p: { xs: 1.5, md: 2 },
          pt: 1.5,
        }}
      >
        {/* Izquierda: herramientas + pastillas */}
        <Box
          sx={{
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            overflow: "hidden",
            maxHeight: { xs: 280, md: "none" },
          }}
        >
          <Stack spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              fullWidth
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {t("skillAgent.clientInfo.upload", { defaultValue: "Subir" })}
            </Button>
            <Box
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                uploadFiles(e.dataTransfer?.files);
              }}
              onClick={() => inputRef.current?.click()}
              sx={{
                px: 1.25,
                py: 1.5,
                borderRadius: 2,
                border: "1.5px dashed",
                borderColor: dragOver ? "primary.main" : "divider",
                bgcolor: (th) =>
                  alpha(th.palette.primary.main, dragOver ? 0.12 : 0.04),
                textAlign: "center",
                cursor: "pointer",
                transition: "border-color 0.15s, background-color 0.15s",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ lineHeight: 1.35, display: "block" }}
              >
                {t("skillAgent.clientInfo.dropHintShort", {
                  defaultValue: "Arrastra aquí PDF, fotos…",
                })}
              </Typography>
            </Box>
          </Stack>

          <Stack
            spacing={0.75}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.25,
            }}
          >
            {items.map((item) => {
              const Icon = kindIcon(item.Kind);
              const selected = Number(activeId) === Number(item.id);
              const color = kindColor(item.Kind);
              return (
                <Box
                  key={item.id}
                  onClick={() => openItem(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    width: "100%",
                    minHeight: 36,
                    px: 1,
                    py: 0.6,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(color, selected ? 0.5 : 0.28),
                    bgcolor: selected ? alpha(color, 0.14) : "background.paper",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: alpha(color, selected ? 0.18 : 0.06),
                    },
                    "&:hover .ci-item-trash": { opacity: 1 },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "0.78rem",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.label || item.FileName}
                  </Typography>
                  <Tooltip
                    title={t("skillAgent.clientInfo.deleteItemTooltip", {
                      defaultValue: "Eliminar archivo",
                    })}
                  >
                    <IconButton
                      className="ci-item-trash"
                      size="small"
                      disabled={busy}
                      aria-label={t("skillAgent.clientInfo.deleteItemTooltip", {
                        defaultValue: "Eliminar archivo",
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteItem(item);
                      }}
                      sx={{
                        flexShrink: 0,
                        ml: "auto",
                        opacity: 0.55,
                        color: "error.main",
                        transition: "opacity 0.12s",
                        "&:hover": {
                          bgcolor: (th) => alpha(th.palette.error.main, 0.1),
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
            {!items.length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 0.5 }}
              >
                {t("skillAgent.clientInfo.emptyList", {
                  defaultValue: "Aún no hay archivos.",
                })}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        {/* Derecha: visor */}
        <Box
          sx={{
            minHeight: 0,
            minWidth: 0,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: alpha("#0f172a", 0.03),
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {previewUrl && isImagePreview ? (
            <Stack
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 2,
                px: 0.5,
                py: 0.25,
                borderRadius: 2,
                bgcolor: (th) => alpha(th.palette.background.paper, 0.92),
                border: "1px solid",
                borderColor: "divider",
                boxShadow: `0 4px 14px ${alpha("#0f172a", 0.08)}`,
              }}
            >
              <Tooltip
                title={t("skillAgent.moodboard.zoomOut", {
                  defaultValue: "Alejar",
                })}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={imgZoom <= 0.5}
                    onClick={() =>
                      setImgZoom((z) =>
                        Math.max(0.5, Math.round((z - 0.25) * 100) / 100),
                      )
                    }
                  >
                    <ZoomOutOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ minWidth: 40, textAlign: "center" }}
              >
                {Math.round(imgZoom * 100)}%
              </Typography>
              <Tooltip
                title={t("skillAgent.moodboard.zoomIn", {
                  defaultValue: "Acercar",
                })}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={imgZoom >= 4}
                    onClick={() =>
                      setImgZoom((z) =>
                        Math.min(4, Math.round((z + 0.25) * 100) / 100),
                      )
                    }
                  >
                    <ZoomInOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={t("skillAgent.clientInfo.zoomReset", {
                  defaultValue: "Ajustar",
                })}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={imgZoom === 1}
                    onClick={() => setImgZoom(1)}
                  >
                    <RestartAltOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : null}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              display: "grid",
              placeItems: "center",
              p: 1,
            }}
            onWheel={(e) => {
              if (!isImagePreview || !previewUrl) return;
              if (!(e.ctrlKey || e.metaKey)) return;
              e.preventDefault();
              const dir = e.deltaY > 0 ? -0.1 : 0.1;
              setImgZoom((z) =>
                Math.min(4, Math.max(0.5, Math.round((z + dir) * 100) / 100)),
              );
            }}
          >
            {previewUrl ? (
              isImagePreview ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt=""
                  sx={{
                    display: "block",
                    maxWidth: `${100 * imgZoom}%`,
                    maxHeight: `${100 * imgZoom}%`,
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    userSelect: "none",
                  }}
                />
              ) : previewKind === "pdf" ||
                String(previewMime).includes("pdf") ? (
                <Box
                  component="iframe"
                  src={previewUrl}
                  title="pdf"
                  sx={{
                    width: "100%",
                    height: "100%",
                    border: 0,
                    minHeight: 420,
                    alignSelf: "stretch",
                    justifySelf: "stretch",
                  }}
                />
              ) : previewKind === "video" ||
                String(previewMime).startsWith("video/") ? (
                <Box
                  component="video"
                  src={previewUrl}
                  controls
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Stack spacing={1.5} alignItems="center" sx={{ py: 6, px: 2 }}>
                  <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: 48, color: "text.disabled" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t("skillAgent.clientInfo.openExternal", {
                      defaultValue:
                        "Vista previa no disponible. Descarga el archivo.",
                    })}
                  </Typography>
                  <Button
                    component="a"
                    href={previewUrl}
                    download
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {t("skillAgent.clientInfo.download", {
                      defaultValue: "Descargar",
                    })}
                  </Button>
                </Stack>
              )
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, textAlign: "center" }}
              >
                {items.length
                  ? t("skillAgent.clientInfo.clickPill", {
                      defaultValue:
                        "Pulsa un archivo a la izquierda para abrirlo.",
                    })
                  : t("skillAgent.clientInfo.viewerEmpty", {
                      defaultValue:
                        "Sube o arrastra archivos para verlos aquí.",
                    })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <SkillConfirmDialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        onConfirm={handleDeleteInfo}
        confirming={busy}
        title={t(
          isStatus
            ? "skillAgent.status.deleteDialogTitle"
            : isPresentaciones
              ? "skillAgent.presentaciones.deleteDialogTitle"
              : isProduccion
                ? "skillAgent.produccion.deleteDialogTitle"
                : "skillAgent.clientInfo.deleteDialogTitle",
          {
            defaultValue: isStatus
              ? "¿Eliminar Status?"
              : isPresentaciones
                ? "¿Eliminar Presentaciones PDF?"
                : isProduccion
                  ? "¿Eliminar Producción?"
                  : "¿Eliminar Client Info?",
          },
        )}
        description={t("skillAgent.clientInfo.deleteDialogBody", {
          defaultValue:
            "Se borra «{{name}}» y todos sus archivos. No se puede deshacer.",
          name: String(
            titulo ||
              info?.Titulo ||
              (isStatus
                ? "Status"
                : isPresentaciones
                  ? "Presentaciones PDF"
                  : isProduccion
                    ? "Producción"
                    : "Client Info"),
          ).trim(),
        })}
        eyebrow={t(
          isStatus
            ? "skillAgent.status.deleteEyebrow"
            : isPresentaciones
              ? "skillAgent.presentaciones.deleteEyebrow"
              : isProduccion
                ? "skillAgent.produccion.deleteEyebrow"
                : "skillAgent.clientInfo.deleteEyebrow",
          {
            defaultValue: isStatus
              ? "Compás · STATUS"
              : isPresentaciones
                ? "Compás · PRESENTACIONES PDF"
                : isProduccion
                  ? "Compás · PRODUCCIÓN"
                  : "Compás · CLIENT INFO",
          },
        )}
        confirmLabel={t("skillAgent.common.delete", {
          defaultValue: "Eliminar",
        })}
        cancelLabel={t("skillAgent.common.cancel")}
        icon={DeleteOutlineIcon}
        tone="error"
        confirmColor="error"
      />

      <MeetingDeleteMediaDialog
        open={Boolean(deleteItem)}
        onClose={() => !busy && setDeleteItem(null)}
        onConfirm={handleDeleteItemConfirm}
        deleting={busy}
        isVideo={String(deleteItem?.Kind || "").toLowerCase() === "video"}
        mediaId={deleteItem?.id}
        badge={deleteItemBadge}
        title={t("skillAgent.clientInfo.deleteItemTitle", {
          defaultValue: "¿Eliminar archivo?",
        })}
        hint={t("skillAgent.clientInfo.deleteItemHint", {
          defaultValue: "Se quita del dossier y no se puede deshacer.",
        })}
        body={t("skillAgent.clientInfo.deleteItemBody", {
          defaultValue: "Vas a eliminar «{{name}}».",
          name: String(
            deleteItem?.label || deleteItem?.FileName || "archivo",
          ).trim(),
        })}
        confirmLabel={t("skillAgent.common.delete", {
          defaultValue: "Eliminar",
        })}
      />
    </Box>
  );
}
